import React from 'react';
import { Theme } from '../Theme';

type CollapsiblePanelProps = {
    title: string;
    children: React.ReactNode;
    headerActions?: React.ReactNode;
    defaultCollapsed?: boolean;
    style?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
};

export function CollapsiblePanel(props: CollapsiblePanelProps) {
    const {
        title,
        children,
        headerActions,
        defaultCollapsed = false,
        style,
        contentStyle,
    } = props;
    const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

    return (
        <section style={{ ...styles.container, ...style }}>
            <div style={styles.headerRow}>
                <button
                    type='button'
                    onClick={() => setIsCollapsed((current) => !current)}
                    style={styles.headerButton}
                    aria-expanded={!isCollapsed}
                >
                    <span style={styles.title}>{title}</span>
                </button>
                {headerActions && (
                    <div style={styles.headerActions}>
                        {headerActions}
                    </div>
                )}
                <button
                    type='button'
                    onClick={() => setIsCollapsed((current) => !current)}
                    style={styles.chevronButton}
                    aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                    aria-expanded={!isCollapsed}
                >
                    <span
                        style={{
                            ...styles.chevron,
                            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                        }}
                    >
                        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                            <polyline
                                points='6 9 12 15 18 9'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    </span>
                </button>
            </div>
            <div
                style={{
                    ...styles.divider,
                    opacity: isCollapsed ? 0 : 1,
                }}
            />
            <div
                style={{
                    ...styles.contentOuter,
                    gridTemplateRows: isCollapsed ? '0fr' : '1fr',
                    opacity: isCollapsed ? 0 : 1,
                }}
            >
                <div style={styles.contentInner}>
                    <div style={{ ...styles.content, ...contentStyle }}>
                        {children}
                    </div>
                </div>
            </div>
        </section>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '12px',
        border: Theme.Colours.border,
        overflow: 'hidden',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: Theme.Colours.workArea,
    },
    headerButton: {
        ...Theme.Fonts.h2,
        appearance: 'none',
        WebkitAppearance: 'none',
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '18px 20px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        outline: 'none',
    },
    headerActions: {
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        padding: '0 4px 0 0',
    },
    chevronButton: {
        appearance: 'none',
        WebkitAppearance: 'none',
        width: '44px',
        height: '44px',
        margin: '0 10px 0 0',
        padding: 0,
        border: 'none',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    title: {
        fontSize: '18px',
        fontWeight: 600,
    },
    chevron: {
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease',
        flexShrink: 0,
    },
    divider: {
        borderTop: Theme.Colours.border,
        transition: 'opacity 0.2s ease',
    },
    contentOuter: {
        display: 'grid',
        gridTemplateRows: '1fr',
        transition: 'grid-template-rows 0.2s ease, opacity 0.2s ease',
    },
    contentInner: {
        overflow: 'hidden',
        minHeight: 0,
    },
    content: {
        padding: '20px',
    },
};
